import { useEffect } from "react";

/**
 * @description
 * Computes viewport height and sets it's visual height
 * as a CSS variable on the body element, this is meant
 * to track height when visual keyboard appears and we
 * need to resize the content to mimic native feel.
 *
 * When visual size returns to original, the property is removed.
 * So use css dvh/svh/vh to set the height
 * @example
 * ```css
 * .container {
 *    max-height: var(--viewport-height, 100dvh);
 * }
 * ```
 */
function useViewportHeightListeners() {
  useEffect(() => {
    if (!visualViewport) {
      return undefined;
    }

    // Keep viewport reference for cleanup
    const viewport = visualViewport;

    // Track previous height when resize happens
    let previousHeight =
      document.scrollingElement?.scrollHeight ?? window.innerHeight;

    // When viewport height changes (because virtual keyboard is shown/hidden)
    // if previous height is larger than current viewport height it means
    // the keyboard has been shown, so css property is set with the visual height.
    //
    // Otherwise if viewport height and previous height difference is larger than
    // a specified offset (150, which is based on a guess right now, because larger shift will mean that it's not scroll)
    // we reset the height property to leave the layout css to be used.
    function onViewportResize() {
      if (previousHeight > viewport.height) {
        document.body.style.setProperty(
          "--viewport-height",
          `${viewport.height}px`,
        );

        previousHeight = viewport.height;
      } else if (Math.abs(viewport.height - previousHeight) > 150) {
        document.body.style.removeProperty("--viewport-height");
        previousHeight = window.innerHeight;
      }
    }

    function onViewportScroll() {
      document.body.style.setProperty(
        "--viewport-scroll",
        `${viewport.offsetTop}px`,
      );
    }

    viewport.addEventListener("resize", onViewportResize);
    viewport.addEventListener("scroll", onViewportScroll);

    // Cleanup on unmount
    return () => {
      document.body.style.removeProperty("--viewport-height");
      document.body.style.removeProperty("--viewport-scroll");
      viewport.removeEventListener("resize", onViewportResize);
      viewport.removeEventListener("scroll", onViewportScroll);
    };
  }, []);
}

export default useViewportHeightListeners;
